import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { formatMinor, toMajor, toMinor, type Designer } from '@eprintsmart/shared';
import {
  approveDesignerSettlement, getDesignerSettlement, markDesignerSettlementPaid,
  type SettlementView,
} from '../services/marketplace';

/**
 * One artist's monthly statement.
 *
 * Two steps on purpose — approve, then mark paid — because they are two
 * different facts. Approving freezes what is owed; marking paid records that
 * the transfer left the bank. Collapsing them into one button would mean the
 * only record of a payment is the act of computing it, and a statement that was
 * never actually transferred would look identical to one that was.
 */

const currentPeriod = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export function SettlementDialog({
  designer,
  onClose,
}: {
  designer: Designer | null;
  onClose: () => void;
}) {
  const [period, setPeriod] = useState(currentPeriod());
  const [view, setView] = useState<SettlementView | null>(null);
  const [deductions, setDeductions] = useState('0');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!designer) return;
    setBusy(true);
    setError(null);
    try {
      const next = await getDesignerSettlement({ designerId: designer.id, period });
      setView(next);
      if (next.settlement) {
        setDeductions(String(toMajor(next.settlement.deductionsMinor)));
        setNote(next.settlement.note ?? '');
      } else {
        // TDS at 10% is the usual deduction, so it is offered rather than left
        // for somebody to work out — but it is editable, because whether it
        // applies at all depends on the artist's PAN and their annual total.
        setDeductions(String(toMajor(Math.round(next.grossMinor * 0.1))));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that statement.');
    } finally {
      setBusy(false);
    }
  }, [designer, period]);

  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (!designer) return null;
  const frozen = view?.settlement;
  const paid = frozen?.status === 'PAID';
  const gross = frozen?.grossMinor ?? view?.grossMinor ?? 0;
  const deductionsMinor = toMinor(Number(deductions) || 0);
  const net = frozen ? frozen.netPayableMinor : gross - deductionsMinor;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{designer.displayName} — statement</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!designer.pan && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No PAN on file, so TDS is 20% rather than 10%. Ask them to add it before you pay.
          </Alert>
        )}
        {!designer.payout && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No bank details on file — there is nowhere to send this yet.
          </Alert>
        )}

        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <TextField label="Month" size="small" value={period} disabled={busy}
            onChange={(e) => setPeriod(e.target.value)} placeholder="2026-08" sx={{ width: 140 }} />
          {frozen && (
            <Typography variant="body2" color={paid ? 'success.main' : 'text.secondary'}>
              {paid
                ? `Paid ${frozen.paidAt ? new Date(frozen.paidAt).toLocaleDateString('en-IN') : ''}`
                : 'Approved and frozen — the lines below cannot change now'}
            </Typography>
          )}
        </Stack>

        {!paid && !!view?.heldCount && (
          <Typography variant="body2" color="text.secondary" mb={1}>
            {view.heldCount} more sale{view.heldCount === 1 ? '' : 's'} ({formatMinor(view.heldMinor ?? 0)}) held
            until the order has been delivered for 10 days — they roll into a later month.
          </Typography>
        )}
        {view && view.lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nothing payable for that month yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell><TableCell>Product</TableCell>
                <TableCell>Design</TableCell><TableCell align="right">Qty</TableCell>
                <TableCell align="right">Royalty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view?.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.orderNumber}</TableCell>
                  <TableCell>{l.productName}</TableCell>
                  <TableCell>{l.artworkTitle}</TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell align="right">{formatMinor(l.royaltyMinor)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Box mt={3}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Deductions (TDS)" size="small" value={deductions} disabled={busy || Boolean(frozen)}
              onChange={(e) => setDeductions(e.target.value)} sx={{ width: 180 }}
              helperText="Recorded on the statement, not netted off silently"
            />
            <TextField
              label="Note to the artist" size="small" value={note} disabled={busy || Boolean(frozen)}
              onChange={(e) => setNote(e.target.value)} fullWidth
              helperText="Why the transfer is smaller than the total"
            />
          </Stack>

          <Stack direction="row" spacing={4} mt={2}>
            <Typography variant="body2">Earned <strong>{formatMinor(gross)}</strong></Typography>
            <Typography variant="body2">
              Less deductions <strong>{formatMinor(frozen?.deductionsMinor ?? deductionsMinor)}</strong>
            </Typography>
            <Typography variant="body2">To transfer <strong>{formatMinor(net)}</strong></Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!frozen && (
          <Button variant="outlined" disabled={busy || !view?.lines.length}
            onClick={() => void run(() => approveDesignerSettlement({
              designerId: designer.id, period, deductionsMinor, note: note || undefined,
            }))}>
            Approve
          </Button>
        )}
        {frozen && !paid && (
          <Button variant="contained" disabled={busy}
            onClick={() => void run(() => markDesignerSettlementPaid({ designerId: designer.id, period }))}>
            I have paid {formatMinor(net)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
